import type { ThemeMode } from 'antd-style';
import { AppRouterInstance } from 'next/dist/shared/lib/app-router-context.shared-runtime';
import type { CSSProperties } from 'react';

import type { WSContext } from '@/libs/embed-brige/embed-bridge';

import { CHAT_TEXTAREA_HEIGHT } from '@/const/layout-tokens';
import { LocaleMode } from '@/types_cp/locale';
import { SessionDefaultGroup } from '@/types_cp/session';
import { AsyncLocalStorage } from '@/utils_cp/local-storage';

export enum SidebarTabKey {
  Chat = 'chat',
}

export enum ChatSettingsTabs {
  Chat = 'chat',
}

export enum SettingsTabs {
  About = 'about',
  Agent = 'agent',
  Common = 'common',
  Hotkey = 'hotkey',
  LLM = 'llm',
  Provider = 'provider',
  Proxy = 'proxy',
  Storage = 'storage',
  Sync = 'sync',
  SystemAgent = 'system-agent',
  TTS = 'tts',
}

export enum ProfileTabs {
  Profile = 'profile',
  Security = 'security',
  Stats = 'stats',
}

export interface SystemStatus {
  // which sessionGroup should expand
  expandSessionGroupKeys: string[];
  filePanelWidth: number;
  hidePWAInstaller?: boolean;
  hideThreadLimitAlert?: boolean;
  imagePanelWidth: number;
  imageTopicPanelWidth?: number;
  inputHeight: number;
  isEmbedIframe?: boolean;

  isEnablePglite?: boolean;
  isShowCredit?: boolean;
  language?: LocaleMode;
  latestChangelogId?: string;
  mobileShowPortal?: boolean;
  mobileShowTopic?: boolean;
  portalWidth: number;
  conversationWidth: number;
  showChatSideBar?: boolean;
  showFilePanel?: boolean;
  showHotkeyHelper?: boolean;
  showImagePanel?: boolean;
  showImageTopicPanel?: boolean;
  showConversationPanel?: boolean;
  showSystemRole?: boolean;
  systemRoleExpandedMap: Record<string, boolean>;
  themeMode?: ThemeMode;
  zenMode?: boolean;
  showHeader?: boolean;
  showSidebar?: boolean;
  sideBarStyle?: CSSProperties;
  messageListStyle?: CSSProperties;
  showGlobalLoading?: boolean;
  showSidebarToggle?: boolean;
}

export interface EmbedParams {
  app_id: number;
  debug: boolean;
  mode: string | null;
  organization_id: string | null;
  source: string | null;
  workspace_id: string | null;
}

export interface GlobalState {
  embeddedAppUrl?: string | null;
  workspaceContext?: WSContext | null;
  embedParams?: EmbedParams;
  hasNewVersion?: boolean;
  isMobile?: boolean;
  isStatusInit?: boolean;
  latestVersion?: string;
  router?: AppRouterInstance;
  sidebarKey: SidebarTabKey;
  status: SystemStatus;
  statusStorage: AsyncLocalStorage<SystemStatus>;
}

export const INITIAL_STATUS = {
  expandSessionGroupKeys: [SessionDefaultGroup.Pinned, SessionDefaultGroup.Default],
  filePanelWidth: 320,
  hidePWAInstaller: false,
  hideThreadLimitAlert: false,
  imagePanelWidth: 320,
  imageTopicPanelWidth: 80,
  inputHeight: CHAT_TEXTAREA_HEIGHT,
  isEmbedIframe: false,
  language: 'auto' as LocaleMode,
  mobileShowTopic: false,
  portalWidth: 400,
  conversationWidth: 192,
  showChatSideBar: false,
  showFilePanel: true,
  showHotkeyHelper: false,
  showImagePanel: true,
  showImageTopicPanel: true,
  showConversationPanel: true,
  showSystemRole: false,
  systemRoleExpandedMap: {},
  themeMode: 'auto',
  zenMode: false,
  showHeader: true,
  showSidebar: true,
  sideBarStyle: undefined,
  messageListStyle: undefined,
  showGlobalLoading: true,
  showSidebarToggle: false,
} satisfies SystemStatus;

export const initialState: GlobalState = {
  embeddedAppUrl: null,
  workspaceContext: null,
  isMobile: false,
  isStatusInit: false,
  sidebarKey: SidebarTabKey.Chat,
  status: INITIAL_STATUS,
  statusStorage: new AsyncLocalStorage('SYSTEM_STATUS'),
};
